/**
 * Facturen Overzicht - Lijst van alle verkoopfacturen
 */

import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { facturenService, FactuurListItem, FactuurStatus } from '../../services/facturen.service';

const FacturenList = () => {
  const navigate = useNavigate();
  const [facturen, setFacturen] = useState<FactuurListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<FactuurStatus | ''>('');
  const [generatingPdf, setGeneratingPdf] = useState<number | null>(null);

  useEffect(() => {
    loadFacturen();
  }, [statusFilter]);

  const loadFacturen = async () => {
    try {
      setLoading(true);
      const filters = statusFilter ? { status: statusFilter as FactuurStatus } : undefined;
      const data = await facturenService.getAll(filters);
      setFacturen(data);
      setError(null);
    } catch (err: any) {
      setError(err.message || 'Kon facturen niet laden');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateStr: string) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleDateString('nl-NL');
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('nl-NL', {
      style: 'currency',
      currency: 'EUR'
    }).format(amount);
  };

  const handleDelete = async (e: React.MouseEvent, factuur: FactuurListItem) => {
    e.stopPropagation();
    if (!window.confirm(`Weet je zeker dat je factuur ${factuur.factuurnummer} wilt verwijderen?`)) {
      return;
    }
    try {
      await facturenService.delete(factuur.id);
      loadFacturen();
    } catch (err: any) {
      setError(err.message || 'Kon factuur niet verwijderen');
    }
  };

  const handleGenereerPdf = async (e: React.MouseEvent, factuur: FactuurListItem) => {
    e.stopPropagation();
    setGeneratingPdf(factuur.id);
    
    try {
      await facturenService.genereerPdf(factuur.id);
      alert(`PDF gegenereerd voor factuur ${factuur.factuurnummer}`);
    } catch (err: any) {
      setError(err.message || 'Kon PDF niet genereren');
    } finally {
      setGeneratingPdf(null);
    }
  };

  const getStatusBadge = (status: FactuurStatus) => {
    const styles: Record<FactuurStatus, { bg: string; color: string; label: string }> = {
      CONCEPT: { bg: '#f3f4f6', color: '#4b5563', label: 'Concept' },
      VERSTUURD: { bg: '#dbeafe', color: '#1d4ed8', label: 'Verstuurd' },
      BETAALD: { bg: '#d1fae5', color: '#059669', label: 'Betaald' },
      DEELS_BETAALD: { bg: '#fef3c7', color: '#d97706', label: 'Deels betaald' },
      VERVALLEN: { bg: '#fee2e2', color: '#dc2626', label: 'Vervallen' },
      GEANNULEERD: { bg: '#f3f4f6', color: '#6b7280', label: 'Geannuleerd' },
      CREDITNOTA: { bg: '#e0e7ff', color: '#4338ca', label: 'Creditnota' }
    };

    const style = styles[status] || styles.CONCEPT;

    return (
      <span style={{
        padding: '0.25rem 0.75rem',
        borderRadius: '9999px',
        fontSize: '0.85rem',
        fontWeight: 500,
        backgroundColor: style.bg,
        color: style.color
      }}>
        {style.label}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="page-container">
        <div style={{ textAlign: 'center', padding: '3rem' }}>Laden...</div>
      </div>
    );
  }

  return (
    <div className="page-container">
      <div className="page-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
        <h1 style={{ margin: 0 }}>Verkoopfacturen</h1>
        <button
          className="btn btn-primary"
          onClick={() => navigate('/facturen/new')}
        >
          + Nieuwe Factuur
        </button>
      </div>

      {error && (
        <div style={{ background: '#fee2e2', color: '#dc2626', padding: '1rem', borderRadius: '8px', marginBottom: '1rem' }}>
          {error}
        </div>
      )}

      {/* Filters */}
      <div style={{ marginBottom: '1rem', display: 'flex', gap: '1rem', alignItems: 'center' }}>
        <label style={{ fontWeight: 500 }}>Status:</label>
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value as FactuurStatus | '')}
          style={{ padding: '0.5rem', borderRadius: '6px', border: '1px solid #d1d5db' }}
        >
          <option value="">Alle</option>
          <option value="CONCEPT">Concept</option>
          <option value="VERSTUURD">Verstuurd</option>
          <option value="BETAALD">Betaald</option>
          <option value="DEELS_BETAALD">Deels betaald</option>
          <option value="VERVALLEN">Vervallen</option>
          <option value="GEANNULEERD">Geannuleerd</option>
        </select>
        <span style={{ color: '#6b7280' }}>
          {facturen.length} {facturen.length === 1 ? 'factuur' : 'facturen'}
        </span>
      </div>

      {/* Tabel */}
      <div style={{ background: 'white', borderRadius: '12px', overflow: 'hidden', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
        {facturen.length === 0 ? (
          <div style={{ padding: '3rem', textAlign: 'center', color: '#6b7280' }}>
            Geen facturen gevonden
          </div>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: '#f9fafb', borderBottom: '1px solid #e5e7eb' }}>
                <th style={{ padding: '1rem', textAlign: 'left', fontWeight: 600 }}>Factuurnummer</th>
                <th style={{ padding: '1rem', textAlign: 'left', fontWeight: 600 }}>Klant</th>
                <th style={{ padding: '1rem', textAlign: 'left', fontWeight: 600 }}>Datum</th>
                <th style={{ padding: '1rem', textAlign: 'left', fontWeight: 600 }}>Vervaldatum</th>
                <th style={{ padding: '1rem', textAlign: 'left', fontWeight: 600 }}>Status</th>
                <th style={{ padding: '1rem', textAlign: 'right', fontWeight: 600 }}>Totaal</th>
                <th style={{ padding: '1rem', textAlign: 'right', fontWeight: 600 }}>Openstaand</th>
                <th style={{ padding: '1rem', textAlign: 'center', fontWeight: 600 }}>Acties</th>
              </tr>
            </thead>
            <tbody>
              {facturen.map(factuur => (
                <tr
                  key={factuur.id}
                  style={{ borderBottom: '1px solid #e5e7eb', cursor: 'pointer' }}
                  onClick={() => navigate(`/facturen/${factuur.id}`)}
                  onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f9fafb'}
                  onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'white'}
                >
                  <td style={{ padding: '1rem', fontWeight: 500 }}>{factuur.factuurnummer}</td>
                  <td style={{ padding: '1rem' }}>{factuur.relatie_naam || '-'}</td>
                  <td style={{ padding: '1rem' }}>{formatDate(factuur.factuur_datum)}</td>
                  <td style={{ padding: '1rem' }}>{formatDate(factuur.vervaldatum)}</td>
                  <td style={{ padding: '1rem' }}>{getStatusBadge(factuur.status)}</td>
                  <td style={{ padding: '1rem', textAlign: 'right', fontWeight: 500 }}>
                    {formatCurrency(factuur.totaal)}
                  </td>
                  <td style={{
                    padding: '1rem',
                    textAlign: 'right',
                    fontWeight: 500,
                    color: factuur.bedrag_openstaand > 0 ? '#dc2626' : '#059669'
                  }}>
                    {formatCurrency(factuur.bedrag_openstaand)}
                  </td>
                  <td style={{ padding: '1rem', textAlign: 'center' }}>
                    <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'center' }}>
                      <button
                        className="btn btn-sm btn-secondary"
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/facturen/${factuur.id}`);
                        }}
                      >
                        Bekijken
                      </button>
                      <button
                        className="btn btn-sm btn-primary"
                        onClick={(e) => handleGenereerPdf(e, factuur)}
                        disabled={generatingPdf === factuur.id}
                        style={{ backgroundColor: '#059669', borderColor: '#059669' }}
                      >
                        {generatingPdf === factuur.id ? 'PDF...' : 'PDF'}
                      </button>
                      {factuur.status === 'CONCEPT' && (
                        <button
                          className="btn btn-sm"
                          style={{ backgroundColor: '#fee2e2', color: '#dc2626' }}
                          onClick={(e) => handleDelete(e, factuur)}
                        >
                          Verwijderen
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

export default FacturenList;
